import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Stack,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon,
  FactCheck as GradeIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { analyzeEvidence } from '../../api/evidenceApi';
import { loadRemoteProgress, saveRemoteProgress } from '../../api/progressApi';

const TRACKER_ID = 'dq-math-02-missingness-practice-bank';

const LENSES = [
  ['detection_rule', 'Detection rule'],
  ['evidence', 'Evidence'],
  ['business_check', 'Business check'],
  ['root_cause', 'Root cause'],
  ['treatment', 'Treatment'],
  ['caution', 'Caution'],
  ['verify', 'Verify'],
  ['learner_sentence', 'Learner sentence'],
];

const WORKING_PROMPTS = {
  M1: 'Count the blank age cells and compute the missingness rate (blanks / total rows). Then check whether the gap lines up with signup_channel before deciding MCAR vs MAR.',
  M2: 'First decide whether delivery_date is even supposed to exist for digital products (check delivery_required). Then compute the completeness rate using only the rows where it applies.',
  M3: 'Compute the raw blank rate for payment_date, then check whether every blank lines up with status = open before concluding it is MAR rather than a defect.',
  M4: 'Compute the blank rate for region, then check whether every blank shares the same source_system and load_date — a clean split points to a source-system defect, not random loss.',
  M5: "Count how many salary values are a placeholder ('REDACTED') rather than a real number, then check whether every one lines up with department = Executive.",
  M6: 'Compute the blank rate separately for vehicle_reg and property_postcode, and check whether each lines up exactly with claim_type before concluding anything is missing.',
  M7: 'Count how many age values equal 999 (a placeholder, not a true blank), then check whether it lines up with channel = phone.',
  M8: 'List the store-date combinations you would expect to see, then compare against what is actually present to find the missing row — this is a different check from a blank cell inside an existing row.',
};

const PRACTICE_TABLES = [
  {
    id: 'M1',
    title: 'Practice Table M1',
    theme: 'Retail customer profile',
    tables: [{
      columns: ['customer_id', 'signup_channel', 'age', 'loyalty_tier'],
      rows: [
        ['C001', 'web', '34', 'Gold'],
        ['C002', 'store', '', 'Silver'],
        ['C003', 'web', '28', 'Bronze'],
        ['C004', 'phone', '', 'Silver'],
      ],
    }],
  },
  {
    id: 'M2',
    title: 'Practice Table M2',
    theme: 'Ecommerce delivery',
    tables: [{
      columns: ['order_id', 'product_type', 'delivery_required', 'delivery_date'],
      rows: [
        ['O001', 'physical', 'true', '2026-07-02'],
        ['O002', 'digital', 'false', ''],
        ['O003', 'physical', 'true', '2026-07-04'],
        ['O004', 'digital', 'false', ''],
      ],
    }],
  },
  {
    id: 'M3',
    title: 'Practice Table M3',
    theme: 'Invoice payment lifecycle',
    tables: [{
      columns: ['invoice_id', 'status', 'invoice_date', 'payment_date'],
      rows: [
        ['I001', 'paid', '2026-07-01', '2026-07-03'],
        ['I002', 'open', '2026-07-05', ''],
        ['I003', 'paid', '2026-07-06', '2026-07-08'],
        ['I004', 'open', '2026-07-09', ''],
      ],
    }],
  },
  {
    id: 'M4',
    title: 'Practice Table M4',
    theme: 'CRM source migration',
    tables: [{
      columns: ['order_id', 'load_date', 'source_system', 'region'],
      rows: [
        ['O101', '2026-07-01', 'CRM_A', 'North'],
        ['O102', '2026-07-01', 'CRM_A', 'South'],
        ['O103', '2026-07-02', 'CRM_B', ''],
        ['O104', '2026-07-02', 'CRM_B', ''],
      ],
    }],
  },
  {
    id: 'M5',
    title: 'Practice Table M5',
    theme: 'Salary reporting',
    tables: [{
      columns: ['employee_id', 'department', 'salary'],
      rows: [
        ['E001', 'Finance', '42000'],
        ['E002', 'Executive', 'REDACTED'],
        ['E003', 'Operations', '36000'],
        ['E004', 'Executive', 'REDACTED'],
      ],
    }],
  },
  {
    id: 'M6',
    title: 'Practice Table M6',
    theme: 'Insurance claims',
    tables: [{
      columns: ['claim_id', 'claim_type', 'vehicle_reg', 'property_postcode'],
      rows: [
        ['CL01', 'motor', 'AB12 CDE', ''],
        ['CL02', 'home', '', 'M4 8TT'],
        ['CL03', 'motor', 'XY98 ZZZ', ''],
        ['CL04', 'home', '', 'LS2 9AA'],
      ],
    }],
  },
  {
    id: 'M7',
    title: 'Practice Table M7',
    theme: 'Customer age import',
    tables: [{
      columns: ['customer_id', 'age', 'channel'],
      rows: [
        ['C001', '31', 'web'],
        ['C002', '999', 'phone'],
        ['C003', '45', 'store'],
        ['C004', '999', 'phone'],
      ],
    }],
  },
  {
    id: 'M8',
    title: 'Practice Table M8',
    theme: 'Store daily sales grid',
    tables: [{
      columns: ['store_id', 'sales_date', 'revenue'],
      rows: [
        ['S01', '2026-07-01', '1200'],
        ['S01', '2026-07-02', '1180'],
        ['S02', '2026-07-01', '900'],
        ['S02', '2026-07-03', '950'],
      ],
    }],
  },
];

const RUBRIC_TERMS = {
  M1: {
    detection: ['2/4', '50%', 'blank', 'rate'],
    bucket: ['unknown value', 'mcar', 'mar'],
    evidence: ['age', 'store', 'phone', 'channel'],
    business: ['signup form', 'required', 'optional', 'channel'],
    treatment: ['flag', 'missing flag', 'keep null', 'compare'],
    verify: ['rate', 'channel', 'missingness'],
    caution: ['do not impute blindly', 'bias', 'check pattern'],
  },
  M2: {
    detection: ['2/4', '50%', 'delivery_required'],
    bucket: ['not applicable', 'structural', 'eligibility'],
    evidence: ['digital', 'delivery_required', 'false'],
    business: ['physical', 'delivery required', 'eligible', 'scope'],
    treatment: ['exclude digital', 'scope', 'document'],
    verify: ['physical only', 'eligible orders'],
    caution: ['do not count digital', 'wrong denominator'],
  },
  M3: {
    detection: ['2/4', '50%', 'open'],
    bucket: ['not yet available', 'pending', 'mar'],
    evidence: ['open', 'payment_date', 'status'],
    business: ['paid invoices', 'open separate', 'lifecycle'],
    treatment: ['keep null', 'pending flag', 'separate'],
    verify: ['paid', 'open', 'lifecycle'],
    caution: ['do not impute payment date', 'not overdue blindly'],
  },
  M4: {
    detection: ['2/4', '50%', 'crm_b'],
    bucket: ['source defect', 'system defect', 'mar'],
    evidence: ['crm_b', 'region', 'load_date'],
    business: ['affected load', 'crm_b', 'batch', 'source_system'],
    treatment: ['quarantine', 'fix mapping', 'reload'],
    verify: ['reload', 'crm_b', 'region populated'],
    caution: ['do not recode unknown', 'do not analyse as normal'],
  },
  M5: {
    detection: ['2/4', '50%', 'redacted'],
    bucket: ['suppressed', 'hidden', 'redacted', 'mar'],
    evidence: ['executive', 'redacted'],
    business: ['policy', 'hr', 'finance', 'approved aggregate'],
    treatment: ['preserve redaction', 'aggregate', 'approved'],
    verify: ['suppression', 'policy', 'aggregate'],
    caution: ['do not impute salary', 'privacy'],
  },
  M6: {
    detection: ['2/4', '50%', 'claim_type'],
    bucket: ['structural', 'not applicable'],
    evidence: ['motor', 'home', 'vehicle', 'property'],
    business: ['claim type', 'motor only', 'home only', 'scope'],
    treatment: ['split', 'subtype', 'scope'],
    verify: ['motor', 'home', 'completeness'],
    caution: ['do not treat as defect', 'wrong denominator'],
  },
  M7: {
    detection: ['2/4', '50%', '999'],
    bucket: ['placeholder', 'sentinel', 'mar'],
    evidence: ['999', 'phone', 'age'],
    business: ['phone intake', 'default value', 'form config'],
    treatment: ['recode null', 'flag', 'fix default'],
    verify: ['999 removed', 'age stats', 'missing rate'],
    caution: ['do not average 999', 'outlier'],
  },
  M8: {
    detection: ['expected', 'grid', 'missing row', 'combination'],
    bucket: ['missing row', 'missing combination'],
    evidence: ['s02', '2026-07-02', 'missing date'],
    business: ['expected grid', 'trading', 'reporting'],
    treatment: ['complete grid', 'investigate', 'flag missing row'],
    verify: ['expected rows', 'store date count'],
    caution: ['do not treat as zero', 'missing is not zero'],
  },
};

export const MISSINGNESS_ANSWER_GUIDE = {
  M1: {
    detection_rule: 'Missingness rate = blank age rows / total rows = 2/4 = 50% (C002, C004).',
    evidence: 'Age is blank for C002 (store) and C004 (phone), while both web customers (C001, C003) have age filled.',
    business_check: 'Confirm whether age is a required field for store/phone signups by checking the signup form config — is it optional there but required on web?',
    root_cause: 'Unknown-value missingness, most likely MCAR or weak MAR — the gap tracks with channel (store/phone) rather than any customer trait, consistent with an optional field on non-web forms.',
    treatment: 'Keep nulls, add age_missing_flag, and investigate the capture process.',
    caution: 'Do not impute age blindly or treat blanks as zero.',
    verify: 'Recompute the age missing rate split by signup_channel and confirm no biased pattern before using age.',
    learner_sentence: 'Age is unknown for half of these customers, split evenly by channel; flag it, compare missing rate by channel, and avoid blind imputation.',
  },
  M2: {
    detection_rule: 'Raw blank rate for delivery_date = 2/4 = 50%, but both blanks are digital orders with delivery_required = false.',
    evidence: 'Digital products (O002, O004) have delivery_required = false and no delivery_date; both physical orders have a date.',
    business_check: 'Scope the denominator to physical (delivery_required = true) orders only — digital products by definition do not need a delivery_date, so counting them overstates the real completeness problem.',
    root_cause: 'Not-applicable missingness (structural), not a data-quality defect — delivery_date simply does not exist as a concept for digital products, so it is missing by design, not by mechanism.',
    treatment: 'Exclude digital products from the delivery-date denominator and document the rule.',
    caution: 'Do not count digital blanks as failed delivery-date capture.',
    verify: 'Confirm physical orders have delivery dates and digital orders are scoped out before reporting completeness.',
    learner_sentence: 'Delivery date is not applicable to digital products, so scope completeness to physical orders only — real completeness is 2/2, not 2/4.',
  },
  M3: {
    detection_rule: 'Raw blank rate for payment_date = 2/4 = 50%; both blanks (I002, I004) have status = open.',
    evidence: 'I002 and I004 are open invoices with a blank payment_date; both paid invoices have one.',
    business_check: 'Scope the denominator to invoices where a payment date is even expected — open invoices have not reached that lifecycle stage yet, so compare paid-invoice completeness separately from open-invoice pending status.',
    root_cause: 'Not-yet-available missingness, MAR — the gap is fully explained by status = open; the value has not been generated yet, it is not lost.',
    treatment: 'Keep nulls, add a pending/open flag, and analyse the two lifecycle stages separately.',
    caution: 'Do not impute payment dates for open invoices.',
    verify: 'Confirm paid invoices have payment dates and open invoices remain correctly flagged as pending.',
    learner_sentence: 'Payment date is pending for open invoices, not missing data; flag open invoices and analyse paid invoices separately.',
  },
  M4: {
    detection_rule: 'Raw blank rate for region = 2/4 = 50%; both blanks (O103, O104) load from source_system = CRM_B on 2026-07-02.',
    evidence: 'CRM_B rows O103 and O104 have a missing region; both CRM_A rows are complete.',
    business_check: 'Scope the check to the CRM_B load specifically — confirm whether other CRM_B loads/dates also show missing region, or if this is isolated to load_date 2026-07-02.',
    root_cause: 'Source-system defect missingness, MAR — the gap follows source_system = CRM_B exactly, pointing to a mapping/ingestion fault in that load rather than a random gap.',
    treatment: 'Quarantine the CRM_B load, fix the mapping, and reload if possible.',
    caution: 'Do not recode CRM_B regions as Unknown without investigating the source defect first.',
    verify: 'Confirm CRM_B region values populate after reload and disclose the affected period.',
    learner_sentence: 'CRM_B caused the missing region values; quarantine the load, fix mapping, reload, and verify region completeness.',
  },
  M5: {
    detection_rule: "Non-numeric placeholder count: 2/4 salary values read 'REDACTED' instead of a number; both are department = Executive.",
    evidence: 'Both Executive-department salaries (E002, E004) show REDACTED instead of a number.',
    business_check: 'Confirm with HR/Finance policy whether executive salary is deliberately withheld from this report, and what aggregate level (if any) is approved to show.',
    root_cause: 'Suppressed/hidden missingness (deliberate), MAR by department — the value exists somewhere but is withheld by policy, not genuinely unrecorded.',
    treatment: 'Preserve the redaction and analyse only at an approved aggregate level.',
    caution: 'Do not impute or expose individual executive salaries.',
    verify: 'Confirm the suppression follows policy and that any aggregate shown does not reveal an individual figure.',
    learner_sentence: 'Executive salaries are deliberately redacted, not missing by accident; preserve the suppression and use approved aggregates only.',
  },
  M6: {
    detection_rule: 'Raw blank rate on vehicle_reg = 2/4 = 50% and on property_postcode = 2/4 = 50%; both split exactly along claim_type.',
    evidence: 'Motor claims (CL01, CL03) have vehicle_reg but no property_postcode; home claims (CL02, CL04) have the reverse.',
    business_check: 'Scope completeness separately per claim_type — vehicle_reg only applies to motor claims and property_postcode only applies to home claims, so neither is really "missing" for the other type.',
    root_cause: 'Structural missingness by claim_type — each field only applies to one claim subtype, so this is a table-design pattern, not a capture defect.',
    treatment: 'Use claim-type-specific rules, or split into subtype tables so each field has a clean denominator.',
    caution: 'Do not treat subtype-specific blanks as data defects.',
    verify: 'Check motor vehicle_reg completeness and home property_postcode completeness as two separate 100% checks.',
    learner_sentence: 'The blank fields are structural by claim type, not defects; measure completeness within motor and home scopes separately.',
  },
  M7: {
    detection_rule: 'Sentinel count: 999 appears in 2/4 age values (50%); both are channel = phone.',
    evidence: 'Age 999 appears for phone customers C002 and C004; web/store customers have plausible ages.',
    business_check: 'Check the phone intake form/default value config — does it submit 999 when no age is entered, unlike web/store?',
    root_cause: 'Placeholder/sentinel missingness, MAR — 999 is not a real age, it is a default value the phone intake system writes when nothing is entered, and it clusters on channel = phone.',
    treatment: 'Convert 999 to null, add a flag, recalculate statistics, and fix the phone intake default.',
    caution: 'Do not average 999 as a real age — it would badly distort the mean.',
    verify: 'Confirm 999 no longer affects age statistics after recoding, and track the ongoing missing rate.',
    learner_sentence: '999 is a placeholder for missing age on phone signups, not a real value; recode to null, flag it, and fix the phone default.',
  },
  M8: {
    detection_rule: 'Expected grid = 2 stores x up to 3 observed dates; S02 is missing its 2026-07-02 row (present for S01).',
    evidence: 'S01 has rows for 07-01, 07-02, and (implied) 07-03; S02 has 07-01 and 07-03 but no 07-02 row at all.',
    business_check: 'Confirm the expected reporting grid (which stores should report on which dates) before concluding data is missing — check whether S02 was closed or not trading on 2026-07-02.',
    root_cause: 'Missing row/combination, not a blank cell — the whole store-date record for S02 on 2026-07-02 never loaded, which is a different failure mode from a blank field inside an existing row.',
    treatment: 'Complete the store-date grid, flag the missing row, and investigate the source load for that date.',
    caution: 'Do not silently treat the missing row as zero revenue.',
    verify: 'Confirm the expected row count against the trading calendar and identify whether S02 2026-07-02 is missing data or a legitimate closure.',
    learner_sentence: 'S02 is missing its entire 2026-07-02 store-date row; complete the grid, flag it, and do not assume zero revenue.',
  },
};

const MISSINGNESS_CRITICAL_TERMS = {
  M1: { evidence: ['C002', 'C004'], learner_sentence: ['age'] },
  M2: { evidence: ['digital'], business_check: ['physical'], learner_sentence: ['digital'] },
  M3: { evidence: ['I002', 'I004'], root_cause: ['open'], learner_sentence: ['open'] },
  M4: { evidence: ['CRM_B'], treatment: ['CRM_B'], learner_sentence: ['CRM_B'] },
  M5: { evidence: ['Executive'], treatment: ['redaction'], learner_sentence: ['redacted'] },
  M6: { evidence: ['motor', 'home'], business_check: ['claim_type'], learner_sentence: ['claim type'] },
  M7: { evidence: ['999'], treatment: ['999'], learner_sentence: ['999'] },
  M8: { evidence: ['S02', '2026-07-02'], business_check: ['store-date'], learner_sentence: ['S02'] },
};

const emptyResponses = () =>
  PRACTICE_TABLES.reduce((acc, table) => {
    acc[table.id] = LENSES.reduce((lensAcc, [key]) => ({ ...lensAcc, [key]: '' }), { approach_working: '' });
    return acc;
  }, {});

const normalise = (value) => String(value || '').toLowerCase();
const hasAny = (value, terms = []) => {
  const text = normalise(value);
  return terms.some((term) => text.includes(String(term).toLowerCase()));
};

const lensScore = (value, terms, fallbackMinLength = 16) => {
  const text = normalise(value).trim();
  if (!text) return 0;
  if (hasAny(text, terms)) return 1;
  return text.length >= fallbackMinLength ? 0.5 : 0;
};

const hasCriticalTerm = (value, term) => {
  const text = String(value || '').toLowerCase();
  const token = String(term || '').toLowerCase();
  if (!token) return true;
  return new RegExp(`(^|[^a-z0-9])${token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}([^a-z0-9]|$)`).test(text);
};

const applyCriticalTermCap = (tableId, key, value, score) => {
  const criticalTerms = MISSINGNESS_CRITICAL_TERMS[tableId]?.[key] || [];
  if (!criticalTerms.length || score <= 0) return { score, reason: '' };
  const missingTerms = criticalTerms.filter((term) => !hasCriticalTerm(value, term));
  if (!missingTerms.length) return { score, reason: '' };
  return { score: Math.min(score, 0.5), reason: `Check exact scenario value: ${missingTerms.join(', ')}.` };
};

const countCompletedTables = (responses) =>
  PRACTICE_TABLES.reduce((count, table) => {
    const row = responses[table.id] || {};
    return count + (String(row.approach_working || '').trim() && LENSES.every(([key]) => String(row[key] || '').trim()) ? 1 : 0);
  }, 0);

const missingRequiredFields = (responses) =>
  PRACTICE_TABLES.map((table) => {
    const row = responses[table.id] || {};
    const missing = [];
    if (!String(row.approach_working || '').trim()) missing.push('Approach / working');
    LENSES.forEach(([key, label]) => {
      if (!String(row[key] || '').trim()) missing.push(label);
    });
    return { table, missing };
  }).filter(({ missing }) => missing.length);

const formatMissingFields = (missingTables = []) =>
  missingTables.slice(0, 4).map(({ table, missing }) => `${table.title}: ${missing.join(', ')}`).join(' | ');

const gradeTable = (tableId, answer = {}) => {
  const rubric = RUBRIC_TERMS[tableId];
  if (!rubric) return null;
  const checks = [
    ['detection_rule', 'Detection rule', lensScore(answer.detection_rule, rubric.detection), answer.detection_rule],
    ['evidence', 'Evidence', lensScore(answer.evidence, rubric.evidence), answer.evidence],
    ['business_check', 'Business check', lensScore(answer.business_check, rubric.business), answer.business_check],
    ['root_cause', 'Root cause', lensScore(answer.root_cause, rubric.bucket, 8), answer.root_cause],
    ['treatment', 'Treatment', lensScore(answer.treatment, rubric.treatment), answer.treatment],
    ['caution', 'Caution', lensScore(answer.caution, rubric.caution), answer.caution],
    ['verify', 'Verify', lensScore(answer.verify, rubric.verify), answer.verify],
    ['learner_sentence', 'Learner sentence', lensScore(answer.learner_sentence, [...rubric.bucket, ...rubric.treatment], 40), answer.learner_sentence],
  ].map(([key, label, score, value]) => {
    const capped = applyCriticalTermCap(tableId, key, value, score);
    return [key, label, capped.score, capped.reason];
  });
  const score = checks.reduce((sum, [, , value]) => sum + value, 0);
  const max = checks.length;
  const missing = checks.filter(([, , value]) => value < 1).map(([, label]) => label);
  const strengths = checks.filter(([, , value]) => value >= 1).map(([, label]) => label);
  const lensResults = checks.reduce((acc, [key, label, value, reason]) => ({
    ...acc,
    [key]: {
      label,
      score: value,
      status: value >= 1 ? 'correct' : value > 0 ? 'partial' : 'missed',
      expected: MISSINGNESS_ANSWER_GUIDE[tableId]?.[key] || '',
      reason,
    },
  }), {});
  return {
    score,
    max,
    percent: Math.round((score / max) * 100),
    strengths,
    missing,
    lensResults,
    feedback: missing.length ? `Review: ${missing.slice(0, 3).join(', ')}${missing.length > 3 ? '...' : ''}.` : 'Strong missingness diagnosis.',
  };
};

const semanticGradeTable = async (table, answer = {}) => {
  const fallback = gradeTable(table.id, answer);
  const lenses = LENSES.map(([key, label]) => ({
    key,
    label,
    user_answer: answer[key] || '',
    expected_answer: MISSINGNESS_ANSWER_GUIDE[table.id]?.[key] || '',
    critical_terms: MISSINGNESS_CRITICAL_TERMS[table.id]?.[key] || [],
  }));
  try {
    const result = await analyzeEvidence({
      content: [
        'Activity: DCC missingness take-home semantic grading',
        `Table: ${table.id}`,
        `Theme: ${table.theme}`,
        `Scenario rows: ${JSON.stringify(table.tables)}`,
        `Learner approach/working: ${answer.approach_working || ''}`,
        `Learner answers: ${JSON.stringify(answer)}`,
        `Expected answers: ${JSON.stringify(MISSINGNESS_ANSWER_GUIDE[table.id] || {})}`,
      ].join('\n'),
      analysisType: 'semantic-rubric-grade',
      context: {
        activity_scope: 'daily_concept_clinic_take_home',
        rubric_key: 'data_quality_missingness',
        table_id: table.id,
        theme: table.theme,
        lenses,
      },
    });
    if (!result?.lensResults) return { ...fallback, mode: 'rules' };
    return {
      ...fallback,
      ...result,
      score: Number(result.score ?? fallback.score),
      max: Number(result.max ?? fallback.max),
      percent: Number(result.percent ?? fallback.percent),
      strengths: result.strengths || fallback.strengths,
      missing: result.missing || fallback.missing,
      lensResults: result.lensResults,
      feedback: result.feedback || fallback.feedback,
      mode: result.mode || 'semantic',
    };
  } catch (error) {
    return { ...fallback, mode: 'rules', llm_error: error.message || 'Semantic grader unavailable.' };
  }
};

const semanticGradeAllTables = async (responses) => {
  const entries = await Promise.all(PRACTICE_TABLES.map(async (table) => [table.id, await semanticGradeTable(table, responses[table.id] || {})]));
  return entries.reduce((acc, [tableId, grade]) => ({ ...acc, [tableId]: grade }), {});
};

const gradeSummary = (grades = {}) => {
  const entries = Object.values(grades).filter(Boolean);
  if (!entries.length) return null;
  const score = entries.reduce((sum, grade) => sum + grade.score, 0);
  const max = entries.reduce((sum, grade) => sum + grade.max, 0);
  const lensCounts = entries.reduce((acc, grade) => {
    (grade.missing || []).forEach((lens) => { acc[lens] = (acc[lens] || 0) + 1; });
    return acc;
  }, {});
  return {
    score,
    max,
    percent: max ? Math.round((score / max) * 100) : 0,
    weaknesses: Object.entries(lensCounts).sort((a, b) => b[1] - a[1]).slice(0, 3).map(([lens]) => lens),
  };
};

function DataTable({ table }) {
  const theme = useTheme();
  return (
    <Box sx={{ my: 1.5 }}>
      <Box sx={{ overflowX: 'auto' }}>
        <Box component="table" sx={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '0.84rem',
          '& th': { bgcolor: alpha(theme.palette.primary.main, 0.09), fontWeight: 800 },
          '& th, & td': { border: `1px solid ${theme.palette.divider}`, px: 1.25, py: 0.85, textAlign: 'left', verticalAlign: 'top' },
        }}>
          <thead><tr>{table.columns.map((column) => <th key={column}>{column}</th>)}</tr></thead>
          <tbody>{table.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={`${rowIndex}-${cellIndex}`}>{cell}</td>)}</tr>
          ))}</tbody>
        </Box>
      </Box>
    </Box>
  );
}

export default function MissingnessPracticeBank() {
  const theme = useTheme();
  const [responses, setResponses] = useState(() => emptyResponses());
  const [grades, setGrades] = useState({});
  const [loadState, setLoadState] = useState('loading');
  const [saveState, setSaveState] = useState('idle');
  const [gradeState, setGradeState] = useState('idle');
  const [message, setMessage] = useState('');
  const loadedRef = useRef(false);
  const saveTimerRef = useRef(null);

  const completedCount = useMemo(() => countCompletedTables(responses), [responses]);
  const missingTables = useMemo(() => missingRequiredFields(responses), [responses]);
  const canGrade = loadState !== 'loading' && !missingTables.length;
  const summary = useMemo(() => gradeSummary(grades), [grades]);

  useEffect(() => {
    let cancelled = false;
    loadRemoteProgress(TRACKER_ID)
      .then((state) => {
        if (cancelled) return;
        setResponses({ ...emptyResponses(), ...(state?.responses || {}) });
        setGrades(state?.grades || {});
      })
      .catch((error) => {
        if (!cancelled && !String(error?.message || '').includes('Unable to load')) setMessage(error.message || 'Could not load saved answers.');
      })
      .finally(() => {
        if (!cancelled) setLoadState('ready');
        loadedRef.current = true;
      });
    return () => {
      cancelled = true;
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    };
  }, []);

  const saveResponses = async (nextResponses = responses, mode = 'manual', nextGrades = grades) => {
    setSaveState('saving');
    setMessage('');
    try {
      await saveRemoteProgress(TRACKER_ID, {
        version: 1,
        resource: 'dq-math-02-missing-data-sample-tables',
        responses: nextResponses,
        grades: nextGrades,
        grade_summary: gradeSummary(nextGrades),
        completed_tables: countCompletedTables(nextResponses),
        updated_at: new Date().toISOString(),
      });
      setSaveState('saved');
      setMessage(mode === 'auto' ? 'Autosaved.' : 'Saved to your missingness practice record.');
    } catch (error) {
      setSaveState('error');
      setMessage(error.message || 'Could not save your answers.');
    }
  };

  const scheduleSave = (nextResponses, nextGrades = grades) => {
    if (!loadedRef.current) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    setSaveState('dirty');
    saveTimerRef.current = setTimeout(() => saveResponses(nextResponses, 'auto', nextGrades), 900);
  };

  const updateAnswer = (tableId, key, value) => {
    setResponses((current) => {
      const next = { ...current, [tableId]: { ...(current[tableId] || {}), [key]: value } };
      let nextGrades = grades;
      if (Object.keys(grades).length) {
        nextGrades = { ...grades };
        delete nextGrades[tableId];
        setGrades(nextGrades);
        setMessage(`Table ${tableId} changed. Click Grade answers to run semantic grading again.`);
      }
      scheduleSave(next, nextGrades);
      return next;
    });
  };

  const gradeResponses = async () => {
    if (!canGrade) {
      setMessage(`Finish every table before grading. Still missing: ${formatMissingFields(missingTables)}${missingTables.length > 4 ? ' | ...' : ''}`);
      return;
    }
    setGradeState('grading');
    setMessage('Semantic grading in progress...');
    try {
      const nextGrades = await semanticGradeAllTables(responses);
      setGrades(nextGrades);
      await saveResponses(responses, 'manual', nextGrades);
      const usedAi = Object.values(nextGrades).some((grade) => grade?.mode === 'llm');
      setMessage(usedAi ? 'Semantic grading complete.' : 'Grading complete using the rule fallback. AI semantic grader was unavailable.');
    } finally {
      setGradeState('idle');
    }
  };

  return (
    <Box sx={{ mt: 4 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }} justifyContent="space-between" sx={{ p: 2, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: alpha(theme.palette.primary.main, 0.035) }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 0.5 }}>Learner Practice: Savable Missingness Tables</Typography>
          <Typography variant="body2" color="text.secondary">Show your working first, then decide whether each gap is structural, not-yet-available, a source defect, suppressed, or a placeholder. Your work autosaves to your own practice record.</Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
          <Typography variant="caption" color="text.secondary">{completedCount}/{PRACTICE_TABLES.length} complete</Typography>
          {summary && <Chip label={`${summary.percent}%`} color={summary.percent >= 75 ? 'success' : summary.percent >= 50 ? 'warning' : 'default'} size="small" sx={{ fontWeight: 800 }} />}
          <Button variant="outlined" startIcon={<GradeIcon />} onClick={gradeResponses} disabled={saveState === 'saving' || loadState === 'loading' || gradeState === 'grading' || !canGrade} sx={{ textTransform: 'none', fontWeight: 800 }}>
            {gradeState === 'grading' ? 'Grading...' : 'Grade answers'}
          </Button>
          <Button variant="contained" startIcon={saveState === 'saving' ? <CircularProgress color="inherit" size={16} /> : <SaveIcon />} onClick={() => saveResponses()} disabled={saveState === 'saving' || loadState === 'loading'} sx={{ textTransform: 'none', fontWeight: 800 }}>
            {saveState === 'saving' ? 'Saving...' : 'Save now'}
          </Button>
        </Stack>
      </Stack>

      {message && <Alert severity={saveState === 'error' ? 'error' : 'success'} sx={{ mt: 1.5 }}>{message}</Alert>}
      {missingTables.length > 0 && (
        <Alert severity="warning" sx={{ mt: 1.5 }}>
          Grade answers unlocks after all tables are attempted. Still missing: {formatMissingFields(missingTables)}{missingTables.length > 4 ? ' | ...' : ''}
        </Alert>
      )}
      {summary && (
        <Alert severity={summary.percent >= 70 ? 'success' : 'info'} sx={{ mt: 1.5 }}>
          Score: {summary.score}/{summary.max} ({summary.percent}%). {summary.weaknesses.length ? `Main development areas: ${summary.weaknesses.join(', ')}.` : 'No major weak lens detected.'}
        </Alert>
      )}

      <Alert severity="info" sx={{ mt: 1.5 }}>
        Start each table by computing the raw missingness rate (blanks / total rows), then check whether the gap lines up with another column (channel, status, source system) before you classify the root cause. Show that working before filling the Lens.
      </Alert>

      {loadState === 'loading' ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 3 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">Loading your saved answers...</Typography>
        </Stack>
      ) : PRACTICE_TABLES.map((practice) => (
        <Box key={practice.id} sx={{ mt: 3, p: { xs: 1.5, sm: 2 }, border: `1px solid ${theme.palette.divider}`, borderRadius: 1, bgcolor: 'background.paper' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, mb: 0.75 }}>{practice.title}</Typography>
          <Typography variant="body2" sx={{ mb: 1.25 }}><strong>Theme:</strong> {practice.theme}.</Typography>
          {grades[practice.id] && <Alert severity={grades[practice.id].percent >= 70 ? 'success' : 'info'} sx={{ mb: 1.5 }}>{practice.title} score: {grades[practice.id].score}/{grades[practice.id].max} ({grades[practice.id].percent}%). {grades[practice.id].feedback}</Alert>}
          {practice.tables.map((table, index) => <DataTable key={`${practice.id}-${index}`} table={table} />)}
          <Box sx={{ mt: 2, p: 1.5, border: `1px solid ${alpha(theme.palette.info.main, 0.35)}`, borderRadius: 1, bgcolor: alpha(theme.palette.info.main, 0.045) }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.5 }}>Approach / working</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {WORKING_PROMPTS[practice.id] || 'Show the count and rate you used before deciding the root cause and treatment.'}
            </Typography>
            <TextField
              value={responses[practice.id]?.approach_working || ''}
              onChange={(event) => updateAnswer(practice.id, 'approach_working', event.target.value)}
              placeholder="Start with: blanks / total rows = rate. Then show which other column the gap lines up with, and why that points to a specific root cause..."
              size="small"
              fullWidth
              multiline
              minRows={4}
            />
          </Box>
          <Box sx={{ overflowX: 'auto', mt: 2 }}>
            <Box component="table" sx={{
              width: '100%',
              borderCollapse: 'collapse',
              '& th': { bgcolor: alpha(theme.palette.primary.main, 0.09), fontWeight: 800 },
              '& th, & td': { border: `1px solid ${theme.palette.divider}`, px: 1.25, py: 0.9, textAlign: 'left', verticalAlign: 'top' },
              '& th:first-of-type, & td:first-of-type': { width: { xs: 150, sm: 210 } },
            }}>
              <thead><tr><th>Lens</th><th>Answer</th></tr></thead>
              <tbody>{LENSES.map(([key, label]) => {
                const lensResult = grades[practice.id]?.lensResults?.[key] || null;
                const missed = lensResult && lensResult.status !== 'correct';
                return (
                  <tr key={key}>
                    <td>
                      <Stack direction="row" spacing={0.75} alignItems="center">
                        {lensResult?.status === 'correct' && <CheckCircleIcon color="success" fontSize="small" />}
                        {lensResult?.status === 'partial' && <ErrorOutlineIcon color="warning" fontSize="small" />}
                        {lensResult?.status === 'missed' && <ErrorOutlineIcon color="error" fontSize="small" />}
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{label}</Typography>
                      </Stack>
                    </td>
                    <td>
                      <TextField value={responses[practice.id]?.[key] || ''} onChange={(event) => updateAnswer(practice.id, key, event.target.value)} placeholder={`Type your ${label.toLowerCase()}...`} size="small" fullWidth multiline minRows={key === 'learner_sentence' ? 2 : 1} color={lensResult?.status === 'correct' ? 'success' : lensResult?.status === 'partial' ? 'warning' : 'primary'} focused={Boolean(lensResult)} />
                      {missed && (
                        <Alert severity={lensResult.status === 'partial' ? 'warning' : 'error'} sx={{ mt: 0.75, py: 0 }}>
                          <Typography variant="caption" component="div">Expected direction: {lensResult.expected}</Typography>
                          {lensResult.reason && <Typography variant="caption" component="div">{lensResult.reason}</Typography>}
                        </Alert>
                      )}
                    </td>
                  </tr>
                );
              })}</tbody>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
}
